<template>
    <div class="chat-input">
        <!-- File upload preview -->
        <div v-if="selectedFile" class="file-upload-preview">
            <div class="preview-content">
                <img v-if="isImageFile" :src="filePreviewUrl" class="preview-image" />
                <div v-else class="preview-file">
                    <i class="material-icons">insert_drive_file</i>
                    <span>{{ selectedFile.name }}</span>
                </div>
            </div>
            <button class="remove-file-btn" @click="removeFile">
                <i class="material-icons">close</i>
            </button>
        </div>

        <!-- Input area -->
        <div class="input-area">
            <!-- File upload button -->
            <button class="action-btn" @click="triggerFileInput" :disabled="disabled">
                <i class="material-icons">attach_file</i>
            </button>
            <input
                ref="fileInput"
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.webp"
                @change="handleFileSelect"
            />

            <!-- Text input -->
            <textarea
                ref="textInput"
                v-model="messageText"
                class="message-input"
                :placeholder="placeholder"
                :disabled="disabled"
                rows="1"
                @keydown.enter.exact.prevent="sendMessage"
                @keydown.shift.enter="newLine"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
            ></textarea>

            <!-- Send button -->
            <button
                class="send-btn"
                :disabled="!canSend || disabled"
                @click="sendMessage"
            >
                <i class="material-icons">send</i>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChatInput',
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: 'Tapez votre message...'
        },
        maxFileSize: {
            type: Number,
            default: 10 * 1024 * 1024 // 10MB
        }
    },
    data() {
        return {
            messageText: '',
            selectedFile: null,
            filePreviewUrl: null,
            isTyping: false,
            typingTimeout: null
        };
    },
    computed: {
        canSend() {
            return this.messageText.trim().length > 0 || this.selectedFile;
        },
        isImageFile() {
            if (!this.selectedFile) return false;
            return this.selectedFile.type.startsWith('image/');
        }
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file size
            if (file.size > this.maxFileSize) {
                this.$toast.error(`Le fichier ne doit pas dépasser ${this.maxFileSize / 1024 / 1024}MB`);
                return;
            }

            this.selectedFile = file;

            // Create preview URL for images
            if (file.type.startsWith('image/')) {
                this.filePreviewUrl = URL.createObjectURL(file);
            }

            // Reset input
            event.target.value = '';
        },
        removeFile() {
            if (this.filePreviewUrl) {
                URL.revokeObjectURL(this.filePreviewUrl);
            }
            this.selectedFile = null;
            this.filePreviewUrl = null;
        },
        handleInput() {
            // Auto-resize textarea
            const textarea = this.$refs.textInput;
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';

            // Emit typing indicator
            if (!this.isTyping) {
                this.isTyping = true;
                this.$emit('typing-start');
            }

            // Reset typing timeout
            if (this.typingTimeout) {
                clearTimeout(this.typingTimeout);
            }

            this.typingTimeout = setTimeout(() => {
                this.isTyping = false;
                this.$emit('typing-stop');
            }, 2000);
        },
        handleFocus() {
            this.$emit('focus');
        },
        handleBlur() {
            this.$emit('blur');
            if (this.isTyping) {
                this.isTyping = false;
                this.$emit('typing-stop');
            }
        },
        newLine() {
            // Allow shift+enter to create new line
            this.messageText += '\n';
        },
        async sendMessage() {
            if (!this.canSend || this.disabled) return;

            const content = this.messageText.trim();
            const file = this.selectedFile;

            // Clear input immediately for better UX
            this.messageText = '';
            this.removeFile();

            // Reset textarea height
            this.$nextTick(() => {
                if (this.$refs.textInput) {
                    this.$refs.textInput.style.height = 'auto';
                }
            });

            // Stop typing indicator
            if (this.isTyping) {
                this.isTyping = false;
                this.$emit('typing-stop');
            }

            // Emit send event
            if (file) {
                this.$emit('send-file', { file, content });
            } else {
                this.$emit('send', { content, type: 'text' });
            }
        },
        focus() {
            if (this.$refs.textInput) {
                this.$refs.textInput.focus();
            }
        }
    },
    beforeDestroy() {
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }
        if (this.filePreviewUrl) {
            URL.revokeObjectURL(this.filePreviewUrl);
        }
    }
};
</script>

<style scoped>
.chat-input {
    background-color: white;
    border-top: 1px solid #e5e7eb;
}

.file-upload-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}

.preview-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
}

.preview-image {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 6px;
}

.preview-file {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #4b5563;
    font-size: 13px;
}

.preview-file i {
    font-size: 20px;
    color: #6b7280;
}

.preview-file span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.remove-file-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s;
}

.remove-file-btn:hover {
    background-color: #f3f4f6;
    color: #ef4444;
}

.input-area {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 12px;
}

.action-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s;
    flex-shrink: 0;
}

.action-btn:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
}

.action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.action-btn i {
    font-size: 22px;
}

.message-input {
    flex: 1;
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    font-size: 14px;
    line-height: 1.5;
    resize: none;
    outline: none;
    max-height: 120px;
    min-height: 40px;
    transition: border-color 0.2s;
}

.message-input:focus {
    border-color: #059669;
}

.message-input:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
}

.message-input::placeholder {
    color: #9ca3af;
}

.send-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #059669;
    border: none;
    color: white;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s;
    flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
    background-color: #047857;
}

.send-btn:disabled {
    background-color: #d1d5db;
    cursor: not-allowed;
}

.send-btn i {
    font-size: 20px;
}
</style>
